import React from "react";
import tw from "twin.macro";
import { SectionHeading } from "components/misc/Headings.js";
import { PrimaryButton as PrimaryButtonBase } from "components/misc/Buttons.js";
import { Container, ContentWithVerticalPadding } from "components/misc/Layouts.js";
import ProductFrame from "components/misc/ProductFrame.js";
import { ReactComponent as CheckboxIcon } from "feather-icons/dist/icons/check-circle.svg";
import caseViewImage from "images/product/case-view.jpg";

/*
 * Homepage hero.
 *
 * Previously: a stock Unsplash portrait with an unattributed "HR Director,
 * Tech Startup" testimonial. A stock face beside an anonymous quote reads as
 * invented, which is the opposite of what a trust product needs. Replaced
 * with a real screenshot of a caseworker viewing an anonymous report.
 */
const Row = tw.div`flex flex-col lg:flex-row items-center gap-12 lg:gap-16 max-w-screen-xl mx-auto`;
const TextColumn = tw.div`w-full lg:w-1/2 flex-shrink-0`;
const ImageColumn = tw.div`w-full lg:w-1/2`;
const Heading = tw(SectionHeading)`text-left text-primary-900 leading-tight lg:text-5xl`;
const Description = tw.p`mt-6 text-lg leading-relaxed text-gray-800 max-w-xl`;
const FeatureList = tw.ul`mt-6 grid grid-cols-1 sm:grid-cols-2 gap-y-3 gap-x-8`;
const Feature = tw.li`flex items-center`;
const FeatureIcon = tw(CheckboxIcon)`w-5 h-5 text-primary-500 flex-shrink-0`;
const FeatureText = tw.span`ml-2 font-medium text-gray-800`;
const Actions = tw.div`mt-8 flex flex-col sm:flex-row gap-4`;
const PrimaryButton = tw(PrimaryButtonBase)`rounded-full text-center px-8 py-4`;
const SecondaryButton = tw.a`rounded-full text-center px-8 py-4 font-bold border-2 border-primary-500 text-primary-500 hocus:bg-primary-500 hocus:text-white transition duration-300`;
const Caption = tw.p`mt-4 text-sm text-gray-700 text-center`;

export default ({
  description = "EthosHub gives employees a safe way to report bullying, harassment and misconduct — named or fully anonymous — and gives HR the tools to investigate and resolve each case properly.",
  features = ["Anonymous or named reporting", "Two-way anonymous messaging", "Secure evidence uploads", "Case management and audit trail"],
}) => (
  <Container>
    <ContentWithVerticalPadding>
      <Row>
        <TextColumn>
          <Heading>Empower Your People.<br />Improve Your Culture.</Heading>
          <Description>{description}</Description>
          <FeatureList>
            {features.map(f => (
              <Feature key={f}><FeatureIcon /><FeatureText>{f}</FeatureText></Feature>
            ))}
          </FeatureList>
          <Actions>
            <PrimaryButton as="a" href="/contact">Book a demo</PrimaryButton>
            <SecondaryButton href="/PricingPage">See pricing</SecondaryButton>
          </Actions>
        </TextColumn>
        <ImageColumn>
          <ProductFrame
            eager
            src={caseViewImage}
            alt="A caseworker viewing an anonymous bullying report in EthosHub. The reporter is shown only as Anonymous, with a two-way message thread below the incident details."
          />
          <Caption>A caseworker's view of an anonymous report. The reporter can still be messaged — but never identified.</Caption>
        </ImageColumn>
      </Row>
    </ContentWithVerticalPadding>
  </Container>
);
